import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { SECTION_COLOR, pasmoDojezdu } from './ui';

/**
 * Mapa editoru zón (Rozvoz → Zóny).
 *
 * Režimy:
 *  - `prohlizeni` — zóny jako barevné mnohoúhelníky, klik vybere zónu
 *  - `kresleni`   — klik přidá vrchol; klik na první vrchol nebo dvojklik zónu uzavře
 *  - `uprava`     — vrcholy jdou táhnout, „+" mezi nimi přidá vrchol,
 *                   pravé tlačítko / dvojklik na vrchol ho smaže (zůstanou aspoň 3)
 *
 * Táhnutý vrchol se přichytí k vrcholu jiné zóny do 14 px, ať mezi
 * sousedními zónami nevznikají díry. Kreslí se bez knihovny navíc —
 * jen Leaflet, který mapa aut už používá.
 */
const PRICHYT_PX = 14;

function getMapyApiKey() {
    return document.querySelector('meta[name="mapy-api-key"]')?.content || '';
}

export default function ZonyMapa({
    zony = [], vybranaId = null, onVyber, rezim = 'prohlizeni', polygon = [], barva = SECTION_COLOR,
    onPolygon, onDokonceno, dojezdy = null, bistro = null, hledanyBod = null, prizpusobit = 0, isDark,
}) {
    const apiKey = getMapyApiKey();
    const tileUrl = apiKey
        ? `https://api.mapy.com/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${apiKey}`
        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    const attribution = apiKey
        ? '<a href="https://api.mapy.com/copyright" target="_blank">© Seznam.cz a.s.</a>'
        : '© OpenStreetMap';
    const stred = bistro ? [bistro.lat, bistro.lng] : [49.5478, 18.2120];

    return (
        <div className={isDark ? 'flotila-leaflet-dark' : ''} style={{ position: 'relative', height: '100%', width: '100%', isolation: 'isolate', borderRadius: 10, overflow: 'hidden' }}>
            <MapContainer center={stred} zoom={11} style={{ height: '100%', width: '100%', background: isDark ? '#0A0612' : '#f5f5f7' }}
                zoomControl={false} doubleClickZoom={rezim === 'prohlizeni'}>
                <TileLayer url={tileUrl} attribution={attribution} maxZoom={19} />
                <PrizpusobVelikost />
                {dojezdy && <DojezdyVrstva body={dojezdy} />}
                <ZonyVrstva zony={zony} vybranaId={vybranaId} skryjId={rezim !== 'prohlizeni' ? vybranaId : null} onVyber={rezim === 'prohlizeni' ? onVyber : null} jenObrys={!!dojezdy} />
                {rezim !== 'prohlizeni' && (
                    <Editor rezim={rezim} polygon={polygon} barva={barva} onPolygon={onPolygon} onDokonceno={onDokonceno}
                        sousede={zony.filter((z) => z.id !== vybranaId)} />
                )}
                {bistro && <BistroZnacka bistro={bistro} />}
                {hledanyBod && <HledanyBod bod={hledanyBod} />}
                <Prizpusobeni zony={zony} vybranaId={vybranaId} polygon={polygon} bistro={bistro} signal={prizpusobit} />
            </MapContainer>
        </div>
    );
}

function PrizpusobVelikost() {
    const map = useMap();
    useEffect(() => {
        const el = map.getContainer();
        const ro = new ResizeObserver(() => map.invalidateSize());
        ro.observe(el);
        return () => ro.disconnect();
    }, [map]);
    return null;
}

/** Přiblíží na vybranou zónu, jinak na všechny zóny (nebo okolí bistra). */
function Prizpusobeni({ zony, vybranaId, polygon, bistro, signal }) {
    const map = useMap();
    const prvni = useRef(true);
    useEffect(() => {
        const body = (vybranaId && polygon?.length >= 3)
            ? polygon
            : zony.flatMap((z) => (z.aktivni !== false ? z.polygon || [] : []));
        if (body.length >= 3) {
            // Až po přeměření kontejneru — při prvním vykreslení má mapa ještě
            // nulovou velikost a fitBounds by spočítal přiblížení naslepo.
            map.invalidateSize();
            map.fitBounds(L.latLngBounds(body), { padding: [40, 40], animate: !prvni.current, maxZoom: 15 });
            prvni.current = false;
        } else if (bistro && prvni.current) {
            map.setView([bistro.lat, bistro.lng], 12);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, vybranaId, signal, zony.length > 0]);
    return null;
}

/**
 * Mřížka dojezdů jako pásma po 5 minutách — buňky na canvasu, jedna barva,
 * čím blíž, tím sytější. Velikost buňky = rozestup mřížky.
 */
function DojezdyVrstva({ body }) {
    const map = useMap();
    const krok = useMemo(() => {
        const lats = [...new Set(body.map((b) => b[0]))].sort((a, b) => a - b);
        const lngs = [...new Set(body.map((b) => b[1]))].sort((a, b) => a - b);
        const min = (arr) => arr.slice(1).reduce((m, v, i) => Math.min(m, v - arr[i]), Infinity);
        return { lat: Number.isFinite(min(lats)) ? min(lats) : 0.0045, lng: Number.isFinite(min(lngs)) ? min(lngs) : 0.007 };
    }, [body]);
    useEffect(() => {
        const renderer = L.canvas({ padding: 0.2 });
        const skupina = L.layerGroup(body.map(([lat, lng, sec]) => L.rectangle(
            [[lat - krok.lat / 2, lng - krok.lng / 2], [lat + krok.lat / 2, lng + krok.lng / 2]],
            { renderer, stroke: false, fillColor: SECTION_COLOR, fillOpacity: pasmoDojezdu(sec), interactive: false },
        ))).addTo(map);
        return () => { skupina.remove(); };
    }, [map, body, krok]);
    return null;
}

function ZonyVrstva({ zony, vybranaId, skryjId, onVyber, jenObrys = false }) {
    const map = useMap();
    useEffect(() => {
        // Vnější (vyšší pořadí) dřív, ať vnitřní zóna leží navrchu a jde kliknout
        const vrstvy = [...zony]
            .filter((z) => z.id !== skryjId && Array.isArray(z.polygon) && z.polygon.length >= 3)
            .sort((a, b) => (b.poradi ?? 0) - (a.poradi ?? 0))
            .map((z) => {
                const vybrana = z.id === vybranaId;
                const neaktivni = z.aktivni === false;
                const p = L.polygon(z.polygon, {
                    color: z.barva || SECTION_COLOR, weight: vybrana ? 3 : 1.5, dashArray: neaktivni ? '4 6' : null,
                    fillColor: z.barva || SECTION_COLOR, fillOpacity: jenObrys ? 0 : (vybrana ? 0.22 : (neaktivni ? 0.03 : 0.12)),
                    interactive: !!onVyber, bubblingMouseEvents: false,
                });
                p.bindTooltip(z.nazev, { sticky: true, direction: 'top', opacity: 0.9 });
                if (onVyber) p.on('click', () => onVyber(z.id));
                return p.addTo(map);
            });
        return () => vrstvy.forEach((v) => { try { v.remove(); } catch {} });
    }, [map, zony, vybranaId, skryjId, onVyber, jenObrys]);
    return null;
}

const ikonaVrcholu = (prvni, barva) => L.divIcon({
    className: '',
    html: `<div style="width:12px;height:12px;border-radius:3px;background:#fff;border:2px solid ${barva};box-shadow:0 1px 3px rgba(0,0,0,.35);${prvni ? 'transform:scale(1.3);' : ''}"></div>`,
    iconSize: [12, 12], iconAnchor: [6, 6],
});
const ikonaMezi = (barva) => L.divIcon({
    className: '',
    html: `<div style="width:14px;height:14px;border-radius:50%;background:${barva};color:#0A0612;font:700 11px/14px system-ui;text-align:center;opacity:.75;box-shadow:0 1px 3px rgba(0,0,0,.3)">+</div>`,
    iconSize: [14, 14], iconAnchor: [7, 7],
});

function Editor({ rezim, polygon, barva, onPolygon, onDokonceno, sousede }) {
    const map = useMap();
    const polyRef = useRef(polygon);
    polyRef.current = polygon;

    // Kreslení: klik přidá vrchol, dvojklik zónu uzavře
    useMapEvents({
        click(e) {
            if (rezim !== 'kresleni') return;
            onPolygon([...polyRef.current, [Number(e.latlng.lat.toFixed(6)), Number(e.latlng.lng.toFixed(6))]]);
        },
        dblclick() {
            if (rezim === 'kresleni' && polyRef.current.length >= 3) onDokonceno?.();
        },
    });

    const vrcholySousedu = useMemo(() => sousede.flatMap((z) => z.polygon || []), [sousede]);

    useEffect(() => {
        const vrstvy = [];
        if (polygon.length >= 2) {
            vrstvy.push((rezim === 'kresleni' ? L.polyline(polygon, { color: barva, weight: 2.5, dashArray: '6 4', interactive: false })
                : L.polygon(polygon, { color: barva, weight: 3, fillColor: barva, fillOpacity: 0.2, interactive: false })).addTo(map));
        }
        const prichyt = (ll) => {
            const bod = map.latLngToContainerPoint(ll);
            let nej = null, d = PRICHYT_PX;
            for (const v of vrcholySousedu) {
                const vd = bod.distanceTo(map.latLngToContainerPoint(v));
                if (vd < d) { d = vd; nej = v; }
            }
            return nej ? [nej[0], nej[1]] : [Number(ll.lat.toFixed(6)), Number(ll.lng.toFixed(6))];
        };
        polygon.forEach((b, i) => {
            const m = L.marker(b, { icon: ikonaVrcholu(rezim === 'kresleni' && i === 0, barva), draggable: rezim === 'uprava', keyboard: false, bubblingMouseEvents: false });
            if (rezim === 'kresleni' && i === 0) {
                m.on('click', () => { if (polyRef.current.length >= 3) onDokonceno?.(); });
            }
            if (rezim === 'uprava') {
                m.on('dragend', (e) => {
                    const nove = [...polyRef.current];
                    nove[i] = prichyt(e.target.getLatLng());
                    onPolygon(nove);
                });
                const smaz = () => { if (polyRef.current.length > 3) onPolygon(polyRef.current.filter((_, k) => k !== i)); };
                m.on('contextmenu', smaz);
                m.on('dblclick', smaz);
            }
            vrstvy.push(m.addTo(map));
        });
        if (rezim === 'uprava' && polygon.length >= 3) {
            polygon.forEach((b, i) => {
                const c = polygon[(i + 1) % polygon.length];
                const stred = [(b[0] + c[0]) / 2, (b[1] + c[1]) / 2];
                const m = L.marker(stred, { icon: ikonaMezi(barva), draggable: true, keyboard: false, bubblingMouseEvents: false, title: 'Přidat vrchol' });
                const vloz = (ll) => {
                    const nove = [...polyRef.current];
                    nove.splice(i + 1, 0, ll ? prichyt(ll) : stred);
                    onPolygon(nove);
                };
                m.on('click', () => vloz(null));
                m.on('dragend', (e) => vloz(e.target.getLatLng()));
                vrstvy.push(m.addTo(map));
            });
        }
        return () => vrstvy.forEach((v) => { try { v.remove(); } catch {} });
    }, [map, polygon, rezim, barva, vrcholySousedu, onPolygon, onDokonceno]);

    // Kurzor kreslení
    useEffect(() => {
        const el = map.getContainer();
        el.style.cursor = rezim === 'kresleni' ? 'crosshair' : '';
        return () => { el.style.cursor = ''; };
    }, [map, rezim]);

    return null;
}

function BistroZnacka({ bistro }) {
    const map = useMap();
    useEffect(() => {
        const m = L.marker([bistro.lat, bistro.lng], {
            interactive: false, zIndexOffset: 1000,
            icon: L.divIcon({ className: '', iconSize: [18, 18], iconAnchor: [9, 9],
                html: `<div style="width:18px;height:18px;border-radius:50%;background:#0A0612;border:3px solid ${SECTION_COLOR};box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>` }),
        }).addTo(map);
        return () => m.remove();
    }, [map, bistro.lat, bistro.lng]);
    return null;
}

function HledanyBod({ bod }) {
    const map = useMap();
    useEffect(() => {
        const m = L.circleMarker([bod.lat, bod.lng], { radius: 8, color: '#fff', weight: 3, fillColor: bod.zona?.barva || '#EF4444', fillOpacity: 1 }).addTo(map);
        map.panTo([bod.lat, bod.lng]);
        return () => m.remove();
    }, [map, bod]);
    return null;
}
